import { APIService } from "@/services/APIService/APIService";
import { useService } from "@/services/ServiceContext";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import { useSelectedCustomer } from "./Customer";
import { GetRepairOrdersStatusEnum } from "@/services/api";
import * as React from "react";

export function useRepairOrders(status?: GetRepairOrdersStatusEnum) {
	const svc = useService(APIService.serviceKey);
	const { data: customer } = useSelectedCustomer();
	const key = [`work-orders`, customer?.id, status ?? "all"];
	return useSWR(
		key,
		() => {
			if (!customer?.id) return [];
			return svc.repairOrders.getRepairOrders({ customerId: customer?.id, status });
		},
		{
			fallbackData: [],
		}
	);
}

export function useWipRepairOrders(wipId?: string | undefined | null) {
	const repairOrders = useRepairOrders();
	const requests = useWipSubmittedRequests(wipId);

	const mutate = React.useCallback(async () => {
		return await Promise.all([
			(await repairOrders.mutate()) ?? [],
			(await requests.mutate()) ?? [],
		]);
	}, [repairOrders, requests]);

	return React.useMemo(() => {
		const ros = !!wipId ? repairOrders.data?.filter((i) => i.wipId === wipId) || [] : [];
		const hasRequests = requests.data.length > 0;
		return {
			...repairOrders,
			mutate,
			mutateRequests: requests.mutate,
			requests,
			loadingRequests: requests.isLoading,
			validatingRequests: requests.isValidating,
			hasRequests,
			hasRos: ros.length > 0,
			data: ros,
		};
	}, [repairOrders, wipId, requests]);
}

export function useSubmittedRequests() {
	const { data: customer } = useSelectedCustomer();
	const key = [`submitted-ros`, customer?.id];
	const svc = useService(APIService.serviceKey);

	return useSWR(
		key,
		() => {
			if (!customer?.id) return [];
			return svc.requestedRepairOrders.getRequestedRepairOrders({ customerId: customer?.id });
		},
		{
			fallbackData: [],
		}
	);
}
export function useWipSubmittedRequests(wipId?: string | undefined | null) {
	const requests = useSubmittedRequests();

	return React.useMemo(() => {
		return {
			...requests,
			data: !!wipId ? requests.data?.filter((i) => i.wipId === wipId) || [] : [],
		};
	}, [requests]);
}
